import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import announcementConfig from "../../config/announcementConfig";

const storageKey = (userId, announcementId) =>
  `plp-announcement-ack:${userId}:${announcementId}`;

const isAcknowledged = (userId) => {
  if (!userId) return true; // don't show until we know who's asking
  try {
    return (
      window.localStorage.getItem(
        storageKey(userId, announcementConfig.id),
      ) === "true"
    );
  } catch {
    // localStorage unavailable (e.g. private browsing) — fail open so we
    // don't nag on every single page load, just skip the announcement.
    return true;
  }
};

const acknowledge = (userId) => {
  try {
    window.localStorage.setItem(
      storageKey(userId, announcementConfig.id),
      "true",
    );
  } catch {
    // Nothing we can do — the announcement may reappear next visit.
  }
};

/**
 * Full-screen "what's new" announcement, shown once per user.
 *
 * Unlike SplashScreen (site-wide, indefinite, no dismiss), this is
 * acknowledged per-user via localStorage and never shown again after
 * that — see client/src/config/announcementConfig.js to configure or
 * retire it.
 */
function FeatureAnnouncement({ userId }) {
  const [manuallyDismissed, setManuallyDismissed] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const navigate = useNavigate();

  const dismissed =
    manuallyDismissed || !announcementConfig.enabled || isAcknowledged(userId);

  useEffect(() => {
    if (dismissed) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [dismissed]);

  if (!announcementConfig.enabled || dismissed) return null;

  const handleDismiss = () => {
    acknowledge(userId);
    setManuallyDismissed(true);
  };

  const handleCta = () => {
    acknowledge(userId);
    setManuallyDismissed(true);
    if (announcementConfig.ctaPath) {
      navigate(announcementConfig.ctaPath);
    }
  };

  return (
    <div
      className="feature-announcement"
      role="dialog"
      aria-modal="true"
      aria-label={announcementConfig.title}
    >
      <div className="feature-announcement__backdrop" />
      <div className="feature-announcement__card">
        <button
          type="button"
          className="feature-announcement__close"
          onClick={handleDismiss}
          aria-label="Dismiss"
        >
          &times;
        </button>

        {!imageFailed && (
          <img
            className="feature-announcement__image"
            src={announcementConfig.imageSrc}
            alt={announcementConfig.imageAlt}
            onError={() => setImageFailed(true)}
          />
        )}

        <div className="feature-announcement__body">
          <h2>{announcementConfig.title}</h2>
          <p>{announcementConfig.message}</p>

          <div className="feature-announcement__actions">
            {announcementConfig.ctaPath && (
              <button
                type="button"
                className="feature-announcement__cta"
                onClick={handleCta}
              >
                {announcementConfig.ctaLabel}
              </button>
            )}
            <button
              type="button"
              className="feature-announcement__dismiss"
              onClick={handleDismiss}
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FeatureAnnouncement;
